import * as React from "react";

import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";

import { PageHeader, translate } from "jazasoft";

import MuiTable from "../../../components/MuiTable";

import { useDeepEffect, useDeepMemo, useQuery, useSetting, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { OState } from "../../../utils/types";
import { getDistinctValues, isEmpty } from "../../../utils/helpers";
import { findSizeBreakupList, getCutOrderFlowInfoList, getEffFlowInfoList } from "../../../helpers/orderHelper";

import { getCutOrderRows } from "../../cutplan/cutPlanHelper";

const NON_SB_FIELDS = ["destId", "flowRef", "customerFlowRef", "product", "style", "color", "destination", "deliveryDate", "total"];

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  }
});

const GarmentRejectionView = props => {
  const classes = useStyles();
  const { id, translate } = props;

  const [selectedKey, setSelectedKey] = React.useState(null);

  const { allUnitList } = useUnit();
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" } });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList)) return [];
    return getCutOrderRows(order, productParts).map(row => ({
      ...row,
      poRef: order?.poRef,
      customerName: order.customer?.name,
      seasonName: order.season?.name
    }));
  }, [order, productParts]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      // { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }

    return cols;
  }, [flowInfoRows, allUnitList]);

  useDeepEffect(() => {
    if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  

  const orderFlow = flowInfoRows.find(fi => fi.key === selectedKey);
  const cutOrder = orderFlow?.cutOrder || {};

  const { data: rejectionList = [] } = useQuery(
    !isEmpty(cutOrder) && { url: "rejections", params: { search: `orderFlow.id==${cutOrder.id}`, size: 100000 } }
  );

  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

  //** Prepare Size Breakup Columns for View mode **/
  const { columns, headerRows } = useDeepMemo(() => {
    const styleCount = getDistinctValues(flowInfoList?.map(fi => fi.style))?.length;
    const colorCount = getDistinctValues(flowInfoList?.map(fi => fi.color))?.length;

    const sbList = findSizeBreakupList(cutOrder, order);

    const sizeGroups = getDistinctValues(sbList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));
    const styleColorIndex = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});

    let columns = [];
    if (styleCount > 1) {
      columns.push({ dataKey: "style", title: "Style", align: "center" });
    }
    if (colorCount > 1) {
      columns.push({ dataKey: "color", title: "Style Color", align: "center" });
    }

    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sbList.length; i++) {
      const sb = sbList[i] || {};
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sb.sizeKey] = sb.sizeGroup;
      }

      sizeList.push({
        dataKey: sb.sizeKey,
        title: sb.size,
        inputType: "text-input",
        options: { type: "number" },
        align: "center",
        leftBorder,
        rightBorder: i === sbList.length - 1,
        render: value => value || 0,
        styleColorIdx: sizeGroups.length > 1 ? styleColorIndex[sb.sizeGroup] : undefined,
        headerCellProps: { style: { borderLeft: leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined } },
        rowCellProps: {
          style: {
            borderLeft: leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
            borderRight: i === sbList.length - 1 ? "1px solid rgba(224, 224, 224, 1)" : undefined
          }
        },
        colSpan: ({ column, row, rowIdx }) =>
          !groupedCutPlan || rowIdx === 1
            ? 1
            : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
      });
    }

    const groupedCutPlan = getDistinctValues(sbList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    columns = [
      ...columns,
      ...sizeList,
      { dataKey: "total", title: "Total", headerCellProps: { style: { borderLeft: "1px solid rgba(224, 224, 224, 1)" } } }
    ];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !groupedCutPlan || rowIdx === 1 || NON_SB_FIELDS.includes(column.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }

    return { columns, headerRows };
  }, [order, cutOrder, flowInfoList]);

  //** Prepare Size Breakup Rows for View mode **/
  const rows = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder)) return [];
    const flowInfoList = getEffFlowInfoList(order, cutOrder);
    const sizeBreakupList = findSizeBreakupList(cutOrder, order);

    const rowList = flowInfoList?.map(flowInfo => {
      const rejList = rejectionList?.filter(e => e.styleColor === flowInfo.styleColor) || [];
      return {
        style: flowInfo.style,
        color: flowInfo.color,
        total: rejList?.reduce((acc, e) => acc + (e.qty || 0), 0),
        ...sizeBreakupList.reduce(
          (acc, sb) => ({ ...acc, [sb.sizeKey]: rejList.filter(e => e.sizeKey === sb.sizeKey).reduce((acc, e) => acc + (e.qty || 0), 0) }),
          {}
        )
      };
    });
    return rowList;
  }, [order, cutOrder, flowInfoList, rejectionList]);

  // const feedbackCompleted = isFeedbackCompleted(cutOrder);

  return (
    <div>
      <PageHeader title="Garment Rejection" />

      <div className={classes.content}>
        <div>
          <MuiTable
            title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
            toolbar={true}
            searchable={true}
            pageable={true}
            searchKeys={["flowRef", "customerFlowRef", "style", "styleNo"]}
            idKey="key"
            parentIdKey="parentKey"
            columns={flowInfoColumns}
            rows={flowInfoRows}
            defaultExpanded={false}
            expandedColor={["#BFDDF8", "#7AB9F0"]}
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
            i18nKey="resources.grouping.fields.flowInfo"
          />
        </div>

        {(!cutOrder || cutOrder.stateValue < OState.MAIN_CUTPLAN_COMPLETED.stateValue) && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Cutting not completed yet. Garment rejection will be done after complete feedback is recieved.</Typography>
          </Alert>
        )}

        <div className={classes.mrTop}>
          <MuiTable
            title="Rejection Qty"
            toolbar={true}
            columns={columns}
            headerRows={headerRows}
            rows={rows}
            fontSize={16}
            headerCellStyle={{ backgroundColor: "#C6D0DA" }}
          />
        </div>
      </div>
    </div>
  );
};

export default translate(GarmentRejectionView);
